import { logger } from '@/utils/logger';
import { getFreshAdSchedule } from '../ads/adsSetup';
import { useJWPlayer, VIDEO_PLAYED_TYPE } from '../JWPlayerContext';
import { JW_CDN_URL } from '../constants';
import { Complete } from '../constants/playEvents';
import { usePlayerEvent } from './usePlayerEvent';

type VideoType =
  | typeof VIDEO_PLAYED_TYPE.NATIVE
  | typeof VIDEO_PLAYED_TYPE.RECOMMENDED
  | typeof VIDEO_PLAYED_TYPE.VERTICAL;

interface UseReplaceNextVideoProps {
  mediaId?: string | null;
  type: VideoType;
  callback?: () => void;
  replaceCurrentVideo?: boolean;
}

export function useReplaceNextVideo({ mediaId, type, callback, replaceCurrentVideo }: UseReplaceNextVideoProps) {
  const { player, playedTypes, updatePlayedTypes, videoPlacementPriority, relatedPluginLoaded } = useJWPlayer();
  const canPlayType = (type: VideoType): boolean => {
    const typeIndex = videoPlacementPriority.indexOf(type);
    return videoPlacementPriority.slice(0, typeIndex).every((priorType) => playedTypes.includes(priorType));
  };

  const fetchMediaInfo = async (mediaId: string) => {
    const data = await fetch(`${JW_CDN_URL}/media/${mediaId}`);

    const {
      playlist: [media],
    } = await data.json();

    // The player's ad config is only built at setup, so request new bids/ad tag for this video
    const adschedule = await getFreshAdSchedule(player).catch((error) => {
      logger.catch(`getFreshAdSchedule error: `, error);
      return undefined;
    });

    return adschedule ? { ...media, adschedule } : media;
  };

  const replaceVideo = async (mediaId: string) => {
    if (canPlayType(type)) {
      if (replaceCurrentVideo || relatedPluginLoaded) {
        player?.setPlaylistItemCallback(async () => {
          const media = await fetchMediaInfo(mediaId).catch((error) => {
            logger.catch(`setPlaylistItemCallback error: `, error);
            return;
          });

          if (callback) callback();
          if (!playedTypes.includes(type)) updatePlayedTypes(type);

          player?.removePlaylistItemCallback();
          return media;
        });
      } else {
        // This is a (temporary) workaround for a JW bug where the `related` plugin is not loaded
        // when the JW strategy reels player contains the same video, so no next video is
        // playing and `player.setPlaylistItemCallback` is not called, so we have to manually replace the video
        const media = await fetchMediaInfo(mediaId).catch((error) => {
          logger.catch(`fetchMediaInfo error: `, error);
          return;
        });

        if (callback) callback();
        if (!playedTypes.includes(type)) updatePlayedTypes(type);

        player.load([media]);
        player.playlistItem(0);
      }
    }
  };

  if (replaceCurrentVideo && mediaId) replaceVideo(mediaId);

  usePlayerEvent(player, Complete, () => {
    if (mediaId) replaceVideo(mediaId);
  });
}
